/**
 * Parent Sailboat Hull — hand-tuned offsets table for a classic cruising
 * sailboat (S&S-style 30-footer archetype). Used as a universal parametric
 * hull that can be linearly scaled to any LOA/BWL/Tc/freeboard.
 *
 * Convention (after scaling):
 *   +X forward (bow), +Y up, +Z starboard. Origin = midship at waterline.
 *
 * Offsets table layout: 11 stations × 7 ring points × [zNorm, yNorm].
 *   stations: i ∈ [0..10], i=0 is transom, i=5 is midship, i=10 is bow tip.
 *   ring points: k ∈ [0..6], k=0 is sheer (deck edge), k=6 is keel centreline.
 *   zNorm ∈ [0..1]: half-beam normalised to (BWL/2). 0 = centreline.
 *   yNorm ∈ [-1..+1+α]: -1 = keel (-Tc), 0 = WL, +1 = midship sheer (+FB),
 *                       values >+1 capture bow rise (sheer line camber).
 *
 * Scaling:
 *   z_actual = zNorm · BWL / 2
 *   y_actual = yNorm ≥ 0 ? yNorm · FB : yNorm · Tc
 *   x_actual = -LOA/2 + (i / 10) · LOA
 */

import {
  MESH_VERTEX_FLOATS,
  MESH_VERTEX_STRIDE,
  type MeshAsset,
} from "../../assets/MeshAsset";

const STATIONS = 11;
const RINGS = 7;

// Hand-tuned offsets for a classic cruising sailboat archetype.
// Based on visual study of S&S 30, Contessa 26, Folkboat-style hulls:
//   - fine forward sections (U → V from midship to bow)
//   - moderate slack bilges
//   - reverse-counter stern with partially immersed transom
//   - sheer line rises ~25 % toward bow, dips ~5 % at stern
//   - bow has slight forward rake (handled by stations 9–10 forward shift)
// prettier-ignore
const PSH_OFFSETS: ReadonlyArray<ReadonlyArray<readonly [number, number]>> = [
  // station 0 — transom (immersed counter, narrow beam, sheer raised)
  [[0.55, 0.95], [0.55, 0.55], [0.54, 0.10], [0.50, -0.10], [0.40, -0.32], [0.20, -0.50], [0.00, -0.55]],
  // station 1 — quarter run
  [[0.78, 0.92], [0.78, 0.52], [0.78, 0.08], [0.72, -0.18], [0.55, -0.50], [0.25, -0.78], [0.00, -0.85]],
  // station 2 — aft body
  [[0.92, 0.90], [0.93, 0.50], [0.93, 0.08], [0.88, -0.20], [0.65, -0.60], [0.30, -0.88], [0.00, -0.95]],
  // station 3 — aft quarter
  [[0.98, 0.90], [0.99, 0.48], [1.00, 0.06], [0.95, -0.22], [0.70, -0.65], [0.32, -0.94], [0.00, -1.00]],
  // station 4 — fore-aft of midship
  [[1.00, 0.92], [1.00, 0.50], [1.00, 0.08], [0.96, -0.22], [0.68, -0.68], [0.30, -0.96], [0.00, -1.00]],
  // station 5 — MIDSHIP (max beam, deepest canoe body)
  [[1.00, 0.95], [1.00, 0.52], [1.00, 0.10], [0.94, -0.22], [0.65, -0.68], [0.28, -0.96], [0.00, -1.00]],
  // station 6 — fore body
  [[0.98, 1.00], [0.98, 0.55], [0.98, 0.13], [0.90, -0.20], [0.58, -0.62], [0.25, -0.90], [0.00, -0.95]],
  // station 7 — fore quarter
  [[0.90, 1.06], [0.90, 0.60], [0.88, 0.18], [0.78, -0.15], [0.46, -0.50], [0.20, -0.75], [0.00, -0.80]],
  // station 8 — entry
  [[0.72, 1.14], [0.72, 0.68], [0.66, 0.25], [0.55, -0.05], [0.32, -0.32], [0.13, -0.55], [0.00, -0.60]],
  // station 9 — fine entry
  [[0.42, 1.22], [0.40, 0.78], [0.32, 0.36], [0.24, 0.10], [0.14, -0.12], [0.05, -0.28], [0.00, -0.32]],
  // station 10 — bow tip / stem head
  [[0.06, 1.30], [0.05, 0.92], [0.04, 0.55], [0.03, 0.20], [0.02, -0.02], [0.01, -0.10], [0.00, -0.10]],
];

// Modern hard-chine cruiser — Antila 28.2 archetype:
//   - plumb stem, pionowa dziobnica
//   - near-vertical topsides: k=0..k=4 all share the same zNorm so the
//     panels from deck to chine are truly vertical — the HARD CHINE
//     appearance comes only from the big z-drop at k=4 → k=5
//   - hard chine running full length (fading past i=7 toward bow)
//   - wide squared transom (~93 % beam)
//   - flat bottom with ~10° deadrise from chine to centreline
//   - LCB ≈ amidships, nearly straight sheer line
//
// Ring layout: k=0 sheer, k=1..k=3 vertical topsides, k=4 chine knuckle
//              (same zNorm as topsides — lowest vertical panel),
//              k=5 bottom edge (big inward jump here), k=6 keel centreline.
//
// yNorm convention (Tc = canoe-body draft, FB = freeboard):
//   Chine at k=4 yNorm ≈ -0.22 → 0.22×Tc ≈ 9 cm below WL (Antila).
//   Keel at transom (i=0) k=6 yNorm=-0.72 → stern body raised (no centreboard
//   trunk extends to transom).
// prettier-ignore
const PSH_MODERN_OFFSETS: ReadonlyArray<ReadonlyArray<readonly [number, number]>> = [
  // i=0 transom — 93 % beam, vertical topsides, shallow raised keel aft
  [[0.93, 0.82], [0.93, 0.46], [0.93, 0.14], [0.93, -0.06], [0.92, -0.20], [0.28, -0.58], [0.00, -0.72]],
  // i=1 quarter run
  [[0.97, 0.86], [0.97, 0.48], [0.97, 0.14], [0.97, -0.08], [0.96, -0.24], [0.28, -0.72], [0.00, -0.88]],
  // i=2 aft body
  [[1.00, 0.88], [1.00, 0.50], [1.00, 0.14], [1.00, -0.10], [1.00, -0.28], [0.28, -0.82], [0.00, -0.96]],
  // i=3 aft quarter
  [[1.00, 0.90], [1.00, 0.50], [1.00, 0.14], [1.00, -0.10], [1.00, -0.28], [0.28, -0.84], [0.00, -1.00]],
  // i=4 forward of midship (max beam maintained)
  [[1.00, 0.92], [1.00, 0.52], [1.00, 0.15], [1.00, -0.10], [1.00, -0.26], [0.27, -0.84], [0.00, -1.00]],
  // i=5 MIDSHIP — max beam, vertical topsides, cleanest chine
  [[1.00, 0.96], [1.00, 0.54], [1.00, 0.15], [1.00, -0.08], [1.00, -0.22], [0.26, -0.80], [0.00, -1.00]],
  // i=6 fore body — full beam maintained, chine still pronounced
  [[0.98, 1.00], [0.98, 0.56], [0.97, 0.18], [0.96, -0.06], [0.96, -0.20], [0.24, -0.78], [0.00, -0.94]],
  // i=7 fore quarter — beam starts narrowing, chine softening
  [[0.89, 1.05], [0.89, 0.60], [0.86, 0.22], [0.82,  0.02], [0.78, -0.12], [0.20, -0.58], [0.00, -0.74]],
  // i=8 entry — chine fading, sections becoming V-shaped
  [[0.66, 1.10], [0.66, 0.66], [0.62, 0.28], [0.53,  0.08], [0.46, -0.08], [0.14, -0.36], [0.00, -0.48]],
  // i=9 fine entry — V section, still some width at deck for a finite stem cap
  [[0.42, 1.18], [0.38, 0.78], [0.30, 0.44], [0.22,  0.18], [0.14,  0.02], [0.05, -0.14], [0.00, -0.22]],
  // i=10 bow tip — plumb stem with a small finite stem cap (avoids knife edge)
  [[0.10, 1.24], [0.09, 0.94], [0.07, 0.58], [0.05,  0.24], [0.03,  0.02], [0.01, -0.04], [0.00, -0.05]],
];

// Bow rake: forward shift (in fractions of LOA) applied to forward stations,
// proportional to height above WL — gives the bow a slight forward overhang.
const BOW_RAKE_FRAC = 0.04; // 4 % of LOA at deck edge
const BOW_RAKE_START_STATION = 8; // stations 8, 9, 10 get raked

export type SailboatHullStyle = "classic" | "modern-chine";

export interface SailboatHullParams {
  /** Length overall (m). */
  LOA: number;
  /** Beam at waterline (m). */
  BWL: number;
  /** Canoe-body draft (m): hull bottom to WL, EXCLUDING keel fin. */
  Tc: number;
  /** Freeboard (m): WL to deck edge at midship. */
  freeboard: number;
  /** Hull side colour. */
  hullColor: [number, number, number];
  /** Deck colour (defaults to hullColor). */
  deckColor?: [number, number, number];
  /**
   * Cove / boot stripe colour. When set, a painted stripe is rendered:
   *   - modern-chine: follows ring k=3 (lowest topside panel, just above chine)
   *   - classic: follows ring k=4 (slack bilge, near WL)
   * Pass undefined to omit the stripe.
   */
  coveStripeColor?: [number, number, number];
  /** Hull archetype. Default "classic". */
  style?: SailboatHullStyle;
}

/**
 * Build a parametric sailboat hull mesh from the parent offsets table.
 * Topology: 11 stations × 7 ring points → port-side strip + mirrored
 * starboard-side strip + transom cap (flat polygon at station 0) +
 * deck cap (flat between port and starboard sheer).
 */
export function buildParentSailboatHull(
  params: SailboatHullParams,
  label = "sailboat-hull",
): MeshAsset {
  const { LOA, BWL, Tc, freeboard: FB } = params;
  const halfL = LOA * 0.5;
  const halfB = BWL * 0.5;
  const deckColor = params.deckColor ?? params.hullColor;
  const style: SailboatHullStyle = params.style ?? "classic";
  const offsets = style === "modern-chine" ? PSH_MODERN_OFFSETS : PSH_OFFSETS;
  const bowRake = style === "modern-chine" ? 0 : BOW_RAKE_FRAC;

  // ---- 1. Materialise station rings in body coords (port side, z ≤ 0) ----
  type Pt = { x: number; y: number; z: number };
  const stationsXYZ: Pt[][] = [];
  for (let i = 0; i < STATIONS; i++) {
    const t = i / (STATIONS - 1); // 0 = transom, 1 = bow tip
    const xBase = -halfL + t * LOA;
    const ring: Pt[] = [];
    const stnOffsets = offsets[i]!;
    for (let k = 0; k < RINGS; k++) {
      const [zNorm, yNorm] = stnOffsets[k]!;
      const z = zNorm * halfB; // half-beam (positive); we'll flip sign for port
      const y = yNorm >= 0 ? yNorm * FB : yNorm * Tc;
      // Bow rake: forward shift proportional to height above WL.
      let xShift = 0;
      if (bowRake > 0 && i >= BOW_RAKE_START_STATION && y > 0) {
        const stnFactor =
          (i - BOW_RAKE_START_STATION) /
          (STATIONS - 1 - BOW_RAKE_START_STATION);
        const heightFactor = Math.min(1, y / FB);
        xShift = bowRake * LOA * stnFactor * heightFactor;
      }
      // Port side has negative z by convention.
      ring.push({ x: xBase + xShift, y, z: -z });
    }
    stationsXYZ.push(ring);
  }

  // ---- 2. Emit triangles with flat shading ------------------------------
  const verts: number[] = [];
  const idx: number[] = [];

  const normal3 = (
    a: [number, number, number],
    b: [number, number, number],
    c: [number, number, number],
  ): [number, number, number] => {
    const ux = b[0] - a[0],
      uy = b[1] - a[1],
      uz = b[2] - a[2];
    const vx = c[0] - a[0],
      vy = c[1] - a[1],
      vz = c[2] - a[2];
    const nx = uy * vz - uz * vy;
    const ny = uz * vx - ux * vz;
    const nz = ux * vy - uy * vx;
    const m = Math.hypot(nx, ny, nz) || 1;
    return [nx / m, ny / m, nz / m];
  };

  const pushV = (
    p: [number, number, number],
    n: [number, number, number],
    c: [number, number, number],
  ): number => {
    const id = verts.length / MESH_VERTEX_FLOATS;
    verts.push(p[0], p[1], p[2], n[0], n[1], n[2], c[0], c[1], c[2]);
    return id;
  };

  const addTri = (
    p0: [number, number, number],
    p1: [number, number, number],
    p2: [number, number, number],
    color: [number, number, number],
  ): void => {
    // Skip degenerate.
    const ax = p1[0] - p0[0],
      ay = p1[1] - p0[1],
      az = p1[2] - p0[2];
    const bx = p2[0] - p0[0],
      by = p2[1] - p0[1],
      bz = p2[2] - p0[2];
    const cx = ay * bz - az * by;
    const cy = az * bx - ax * bz;
    const cz = ax * by - ay * bx;
    if (cx * cx + cy * cy + cz * cz < 1e-12) return;
    const n = normal3(p0, p1, p2);
    idx.push(pushV(p0, n, color), pushV(p1, n, color), pushV(p2, n, color));
  };

  const addQuad = (
    p00: [number, number, number],
    p10: [number, number, number],
    p11: [number, number, number],
    p01: [number, number, number],
    color: [number, number, number],
  ): void => {
    addTri(p00, p10, p11, color);
    addTri(p00, p11, p01, color);
  };

  const hullColor = params.hullColor;

  // ---- 2a. Side strips (port + mirrored starboard) ----------------------
  // Port has z < 0, so outward = -Z. Quad winding a→b→c→d makes
  // tri1=a,b,c with normal in -Z direction (assuming ring k+1 is below k).
  for (let i = 0; i < STATIONS - 1; i++) {
    const s0 = stationsXYZ[i]!;
    const s1 = stationsXYZ[i + 1]!;
    for (let k = 0; k < RINGS - 1; k++) {
      const a = s0[k]!;
      const b = s1[k]!;
      const c = s1[k + 1]!;
      const d = s0[k + 1]!;
      // Port side (z < 0). Outward normal = -Z.
      addQuad(
        [a.x, a.y, a.z],
        [b.x, b.y, b.z],
        [c.x, c.y, c.z],
        [d.x, d.y, d.z],
        hullColor,
      );
      // Starboard mirror (z > 0). Reverse winding so outward = +Z.
      addQuad(
        [a.x, a.y, -a.z],
        [d.x, d.y, -d.z],
        [c.x, c.y, -c.z],
        [b.x, b.y, -b.z],
        hullColor,
      );
    }
  }

  // ---- 2b. Keel seam — close the gap between port and starboard at the
  // last ring (k = RINGS-1). At the keel zNorm should be 0 already, so
  // adjacent z is near zero; emit defensively.
  for (let i = 0; i < STATIONS - 1; i++) {
    const a = stationsXYZ[i]![RINGS - 1]!;
    const b = stationsXYZ[i + 1]![RINGS - 1]!;
    if (Math.abs(a.z) < 1e-4 && Math.abs(b.z) < 1e-4) continue;
    addQuad(
      [a.x, a.y, a.z],
      [b.x, b.y, b.z],
      [b.x, b.y, -b.z],
      [a.x, a.y, -a.z],
      hullColor,
    );
  }

  // ---- 2c. Transom cap (station 0, normal -X = aft-facing) ---------------
  // Use horizontal quad strips (one per ring pair) rather than a fan from
  // poly[0]. Fan triangulation produces degenerate triangles for hard-chine
  // hulls where ring points k=0..k=4 are all at the same zNorm — the three
  // fan vertices then lie on a single line in the transom plane, giving a
  // near-zero cross-product that the degenerate check discards.
  {
    const ring = stationsXYZ[0]!;
    for (let k = 0; k < RINGS - 1; k++) {
      const pk = ring[k]!;
      const pk1 = ring[k + 1]!;
      // Winding: port[k] → port[k+1] → stbd[k+1] → stbd[k] gives normal -X.
      addQuad(
        [pk.x, pk.y, pk.z], // port[k]
        [pk1.x, pk1.y, pk1.z], // port[k+1]
        [pk1.x, pk1.y, -pk1.z], // stbd[k+1]
        [pk.x, pk.y, -pk.z], // stbd[k]
        hullColor,
      );
    }
  }

  // ---- 2d. Deck cap — flat strip between port and starboard sheer -------
  for (let i = 0; i < STATIONS - 1; i++) {
    const a = stationsXYZ[i]![0]!;
    const b = stationsXYZ[i + 1]![0]!;
    addQuad(
      [a.x, a.y, a.z],
      [a.x, a.y, -a.z],
      [b.x, b.y, -b.z],
      [b.x, b.y, b.z],
      deckColor,
    );
  }

  // ---- 2e. Bow cap (station STATIONS-1, normal +X = bow-facing) ----------
  // Only needed when the bow is not a pure knife-edge (zNorm > ~0.01).
  {
    const ring = stationsXYZ[STATIONS - 1]!;
    for (let k = 0; k < RINGS - 1; k++) {
      const pk = ring[k]!;
      const pk1 = ring[k + 1]!;
      // Reversed winding vs transom → normal +X.
      addQuad(
        [pk.x, pk.y, -pk.z], // stbd[k]
        [pk1.x, pk1.y, -pk1.z], // stbd[k+1]
        [pk1.x, pk1.y, pk1.z], // port[k+1]
        [pk.x, pk.y, pk.z], // port[k]
        hullColor,
      );
    }
  }

  // ---- 2f. Cove / boot stripe ------------------------------------------
  // Thin painted band following the actual station geometry.
  // modern-chine: k=3 is the lowest vertical topside edge (just above chine).
  // classic: k=4 sits at the slack bilge / near WL.
  if (params.coveStripeColor) {
    const sc = params.coveStripeColor;
    const stripeRing = style === "modern-chine" ? 3 : 4;
    const stripeH = 0.08; // 8 cm band
    const stripeOut = 0.012; // push out from hull to avoid z-fighting
    for (let i = 0; i < STATIONS - 1; i++) {
      const sA = stationsXYZ[i]![stripeRing]!;
      const sB = stationsXYZ[i + 1]![stripeRing]!;
      const sAlo = stationsXYZ[i]![stripeRing + 1]!;
      const sBlo = stationsXYZ[i + 1]![stripeRing + 1]!;
      const ptA: [number, number, number] = [sA.x, sA.y, sA.z - stripeOut];
      const ptAb: [number, number, number] = [
        sA.x,
        Math.max(sA.y - stripeH, sAlo.y),
        sA.z - stripeOut,
      ];
      const ptB: [number, number, number] = [sB.x, sB.y, sB.z - stripeOut];
      const ptBb: [number, number, number] = [
        sB.x,
        Math.max(sB.y - stripeH, sBlo.y),
        sB.z - stripeOut,
      ];
      // Port side (z < 0)
      addQuad(ptA, ptB, ptBb, ptAb, sc);
      // Starboard mirror
      addQuad(
        [ptA[0], ptA[1], -ptA[2]],
        [ptAb[0], ptAb[1], -ptAb[2]],
        [ptBb[0], ptBb[1], -ptBb[2]],
        [ptB[0], ptB[1], -ptB[2]],
        sc,
      );
    }
    // Transom stripe cap
    {
      const ring = stationsXYZ[0]!;
      const p = ring[stripeRing]!;
      const pl = ring[stripeRing + 1]!;
      const topY = p.y;
      const botY = Math.max(p.y - stripeH, pl.y);
      const x = p.x - stripeOut;
      addQuad(
        [x, topY, p.z],
        [x, botY, p.z],
        [x, botY, -p.z],
        [x, topY, -p.z],
        sc,
      );
    }
  }

  return {
    vertices: new Float32Array(verts),
    indices: new Uint32Array(idx),
    stride: MESH_VERTEX_STRIDE,
    label,
  };
}
